/* eslint-disable @typescript-eslint/no-explicit-any */
import { defineStore, acceptHMRUpdate } from 'pinia';
import { useGlobalFiltersStore } from '@/stores/global-filters.store';
import filter from 'lodash/filter';
import get from 'lodash/get';
import { useStarsStore } from '@/stores/stars.store';
import set from 'lodash/set';
import isFunction from 'lodash/isFunction';
import keyBy from 'lodash/keyBy';
import forOwn from 'lodash/forOwn';
import findIndex from 'lodash/findIndex';
import values from 'lodash/values';
import cloneDeep from 'lodash/cloneDeep';
import isNumber from 'lodash/isNumber';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import chunk from 'lodash/chunk';
import { http } from '@pendo/http';
import { fireGuideTrackEvents } from '@/guides/track-events/state-change.js';
import { fireGuideCreatedTrackEvent } from '@/guides/track-events/guide-created/guide-created.js';
import {
    loadGuides,
    loadGuide,
    saveGuide as saveGuideUtil,
    createGuideFromHtml as createGuideFromHtmlUtil,
    saveGuideTranslations,
    deleteGuide,
    bulkPatchGuides,
    saveStep,
    deleteStep,
    saveGuideRedisplay,
    deleteGuideRedisplay,
    isNpsGuide,
    getClassicDesignerTemplates,
    cloneGuideViaEndpoint,
    setGuideDNT as setGuideDNTUtil,
    bulkTranslateGuide as bulkTranslateGuideUtil,
    pollBulkTranslateGuideStatus,
} from '@/vuex/modules/guides/guides.utils.js';
import { canEditGuide } from '@/vuex/modules/guides/guide-permissions.utils';
import {
    getContent,
    getGuideStepsContent,
    getIsArchived,
    isInV1Experiment,
    GUIDE_STATE,
    updateClonedGuideActivationLocation,
    removeLaunchMethodFromGuide,
    getGuideSegmentVisitorCount,
    isDoNotTrackGuide,
} from '@/guides/helpers/common.util.js';
import { AppsService as Apps } from '@/common/apps/apps.service';
import { formatCrossAppDisplayName } from '@/stores/apps.utils.js';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { SdkTools, MinAgentVersion, GuideService, Automations, BuildingBlockPropertiesService } from '@pendo/services/BuildingBlocks';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { isEmailGuide } from '@pendo/services/TaskList';

const { getMinSdkVersionForGuide, ROOT_MIN_SDK_VERSION } = SdkTools;
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { addAppToEntityList } from '@/common/apps/apps.utils';
import {
    importLocalizationFile,
    clearTranslatedStrings,
} from '@/settings/localization-settings/guide-localization.service.js';
import { connectAggregationToState, withComponent, withStore } from '@pendo/agg-connect';
import { NpsPollResponsesAgg } from '@/net-promoter-system/nps-responses-table/nps-responses.aggregation';
import {
    npsFilters,
    gptSummaryMinResponsesLimit,
    gptSummaryMaxResponsesLimit,
    NPS_GPT_SUMMARY_STATUS,
} from '@/net-promoter-system/constants/nps.constants';
import {
    deleteThemeFromList,
    getAllNpsThemes,
    getGptSummaryText,
    getThemeDisplayName,
    renameThemeFromList,
} from '@/net-promoter-system/nps-responses-table/utils';
import { RECENTLY_SELECTED_HISTORY_TYPES } from '@/vuex/modules/user-settings/history/history.constants';
import { isMobileApp } from '@/utils/apps';
import BuildingBlockGuidesService from '@/guides/services/building-blocks/building-block-guides';
import { useGuideCategoriesStore } from '@/stores/guides-guide-categories.store';
import { useGroupsStore } from '@/stores/groups.store';
import { useHistoryStore } from '@/stores/history.store';
import { useThemesStore } from '@/stores/themes.store';

import { useProductLinesStore } from '@/stores/product-lines.store';
import { useSubscriptionStore } from '@/stores/subscription.store';

type BulkTranslateGuideState = 'running' | 'succeeded' | 'failed';

interface BulkTranslateGuideStatus {
    guideId: string;
    jobId?: string;
    state: BulkTranslateGuideState;
    message?: string;
}

interface BulkTranslateGuideStatusResponse {
    data: BulkTranslateGuideStatus;
}

interface HttpErrorLike {
    response?: { status?: number };
    message?: string;
}

let loadAllPromise: Promise<unknown> | null | undefined;
const bulkTranslationStatusPromises: Record<string, Promise<BulkTranslateGuideStatusResponse> | undefined> = {};
const runningBulkTranslationGuideIds = new Set<string>();

interface NpsGptSummary {
    text?: string;
    status?: string;
}

interface AggData {
    data: unknown[];
    status?: string;
    [key: string]: unknown;
}

interface GuidesState {
    editing: boolean;
    cardBeingEdited: unknown;
    saving: boolean;
    fetching: boolean;
    guide: unknown;
    guideTranslationErrors: Record<string, unknown>;
    bulkTranslationStatus: BulkTranslateGuideStatus | null;
    bulkTranslationPoller: string | null;
    bulkTranslationLangCodes: string[];
    map: Record<string, unknown>;
    toAddOrUpdate: unknown[];
    hoveredNpsTheme: string;
    npsResponsesAggData: AggData;
    allNpsThemes: unknown[];
    sideBladeThemeId: unknown;
    sideBladeUpdatedResponse: unknown;
    npsGptSummary: NpsGptSummary;
    describeYourUseCase: string;
    previewLanguage: string;
    shouldUpdateNpsResponses: boolean;
    error: unknown;
    classicDesignerTemplates: unknown[];
}

export function getInitialState (): GuidesState {
    return {
        editing: false,
        cardBeingEdited: null,
        saving: false,
        fetching: false,
        guide: null,
        guideTranslationErrors: {},
        bulkTranslationStatus: null,
        bulkTranslationPoller: null,
        bulkTranslationLangCodes: [],
        map: {},
        toAddOrUpdate: [],
        hoveredNpsTheme: '',
        npsResponsesAggData: { data: [] },
        allNpsThemes: [],
        sideBladeThemeId: null,
        sideBladeUpdatedResponse: null,
        npsGptSummary: {
            text: '',
            status: '',
        },
        describeYourUseCase: '',
        previewLanguage: '',
        shouldUpdateNpsResponses: false,
        error: null,
        classicDesignerTemplates: [],
    };
}

export const useGuidesStore = defineStore('guides', {
    state: getInitialState,
    getters: {
        listAll: (state) => values(state.map).filter((guide: any) => !isEmailGuide(guide)),
        list (): unknown[] {
            return this.listAll.filter((guide: any) => !getIsArchived(guide));
        },
        archivedList: (state) => values(state.map).filter(getIsArchived),
        allGuidesUserCanEdit (): unknown[] {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useAppsStore } = require('@/stores/apps.store');
            const apps = useAppsStore().appMapForActiveSubscription;
            const guides = this.list;
            const permissibleGuides = guides.filter((guide) => {
                return canEditGuide({
                    guide,
                    target: 'ANY',
                    source: 'ANY',
                } as any);
            });

            return addAppToEntityList(permissibleGuides, apps);
        },
        getGuideById: (state) => (id: string) => state.map[id],
        isSaving: (state) => state.saving,
        guidesByAppId (): (appId: number) => unknown[] {
            return (appId: number) => this.list.filter((guide: any) => guide.appId === appId);
        },
        maxPriority (): number {
            const guideList = this.list;
            const maxPriorityArr = guideList.map((guide: any) => {
                if (guide.attributes && isNumber(guide.attributes.priority)) {
                    return guide.attributes.priority;
                }

                return -Infinity;
            });

            return Math.max(Math.max(...maxPriorityArr), guideList.length - 1);
        },
        getNonResourceCenterGuides (): unknown[] {
            return filter(this.list, (guide) => !get(guide, 'attributes.resourceCenter'));
        },
        hasMobileMultiActivations: () => (guide: any) => {
            return !!guide && !!guide.steps[0].activations;
        },
        calcGuideMinSdk (state): ({ ignoreExperimentMinSdk }: { ignoreExperimentMinSdk?: boolean }) => unknown {
            return ({ ignoreExperimentMinSdk = false } = {}) => {
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                const { useAppsStore } = require('@/stores/apps.store');
                const appsStore = useAppsStore();
                const app = appsStore.getAppById((state.guide as any)?.appId);
                const steps = (state.guide as any)?.steps.map(({ buildingBlocks }: any) => ({
                    buildingBlocks: JSON.parse(buildingBlocks),
                }));

                const isAppFlutterCodeless =
                    app?.framework === 'flutter' && !appsStore.isRestrictedMobileApp(app.id);

                return getMinSdkVersionForGuide(
                    {
                        ...(state.guide as any),
                        steps,
                    },
                    undefined,
                    app?.platform,
                    ignoreExperimentMinSdk,
                    { isAppFlutterCodeless },
                );
            };
        },
        getHoveredNpsTheme: (state) => state.hoveredNpsTheme,
        getNpsResponsesAggData: (state) => state.npsResponsesAggData,
        fullNpsThemesWithCounts (state): unknown[] {
            let themesArr: unknown[] = [];
            const aggData = state.npsResponsesAggData.data;

            if (aggData && aggData.length) {
                const countsRow = (aggData as any[]).find((row) => row.counts);

                if (countsRow) {
                    themesArr = countsRow.counts;
                }
            }

            return (themesArr as any[]).filter((theme) => theme.npsTheme);
        },
        npsThemesWithCounts (): unknown[] {
            return (this.fullNpsThemesWithCounts as any[]).filter(
                (t) => t.npsTheme !== npsFilters.noThemeResponses.value,
            );
        },
        getPreviewLanguage: (state) => state.previewLanguage,
        isEmail: (state) => !!get(state, 'guide.attributes.isEmail', false),
        getNpsGuides: (state) => values(state.map).filter((guide) => isNpsGuide(guide)),
        getPollGuides: (state) => values(state.map).filter((guide: any) => guide.polls?.length > 0 && !isNpsGuide(guide)),
        analyticsGuidesListWithStars (): unknown[] {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useAppsStore } = require('@/stores/apps.store');
            const appsStore = useAppsStore();
            const isStarred = useStarsStore().getStarByItemId;

            return this.list.map((guide: any) =>
                Object.freeze({
                    id: guide.id,
                    name: guide.name,
                    state: guide.state,
                    lastUpdatedAt: guide.lastUpdatedAt,
                    appId: guide.appId,
                    appIds: guide.appIds,
                    appIcon: appsStore.getIconByAppId(guide.appId),
                    kind: guide.kind,
                    isStarred: !!isStarred(guide.id),
                    icon: guide.icon,
                }),
            );
        },
        recentGuidesInReporting (state): unknown[] {
            const isStarred = useStarsStore().getStarByItemId;
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useAppsStore } = require('@/stores/apps.store');
            const appsStore = useAppsStore();

            return (useHistoryStore().itemHistory(RECENTLY_SELECTED_HISTORY_TYPES.GUIDE_RECENTLY_SELECTED) ?? [])
                .filter(({ id }: { id: string }) => state.map[id] && !isEmailGuide(state.map[id]) && !getIsArchived(state.map[id]))
                .map(({ id, timestamp }: { id: string; timestamp: number }) => {
                    const guide = state.map[id] as any;

                    return {
                        id: guide.id,
                        name: guide.name,
                        state: guide.state,
                        lastUpdatedAt: guide.lastUpdatedAt,
                        appId: guide.appId,
                        appIds: guide.appIds,
                        appIcon: appsStore.getIconByAppId(guide.appId),
                        appDisplayName: formatCrossAppDisplayName(guide, appsStore.map),
                        kind: guide.kind,
                        isStarred: !!isStarred(guide.id),
                        icon: guide.icon,
                        lastUsed: timestamp,
                    };
                });
        },
        isV1ExperimentsEnabled (): boolean {
            if (!useProductLinesStore().hasProductLinePurchased('guides', 'experimentsEnabled')) {
                return false;
            }

            return this.list.some((guide) => isInV1Experiment(guide));
        },
        isGuideEffectivenessEnabled (): boolean {
            return useProductLinesStore().hasProductLinePurchased('guides', 'guideEffectivenessEnabled');
        },
        isLocalizationEnabled (): (appId: string) => boolean {
            return (appId: string) => {
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                const { useAppsStore } = require('@/stores/apps.store');
                const app = useAppsStore().getAppById(appId);
                const localizationEnabledForApp = get(app, 'applicationFlags.guideLocalization', false);
                const hasPurchasedGuidesLocalization = useProductLinesStore().hasProductLinePurchased(
                    'guides',
                    'localizationEnabled',
                );

                return localizationEnabledForApp && hasPurchasedGuidesLocalization;
            };
        },
    },
    actions: {
        setEditing ({ editing, card }: { editing: boolean; card?: unknown }) {
            this.editing = editing;
            if (editing && card) {
                this.cardBeingEdited = card;
            } else {
                this.cardBeingEdited = null;
            }
        },
        setSaving ({ isSaving }: { isSaving: boolean }) {
            this.saving = isSaving;
        },
        setFetching ({ isFetching }: { isFetching: boolean }) {
            this.fetching = isFetching;
            if (!isFetching) {
                this.updateFromQueue();
            }
        },
        setActiveGuide ({ guide }: { guide: unknown }) {
            this.guide = guide;
        },
        setActiveGuideById ({ id }: { id: string }) {
            this.guide = this.map[id];
        },
        setMap ({ map }: { map: Record<string, unknown> }) {
            this.map = map;
        },
        setError ({ error }: { error: unknown }) {
            this.error = error;
        },
        setGuideTranslationErrors ({ guideId, brokenLangs }: { guideId: string; brokenLangs: unknown }) {
            this.guideTranslationErrors[guideId] = brokenLangs;
        },
        setBulkTranslationStatus ({ status }: { status: BulkTranslateGuideStatus | null }) {
            this.bulkTranslationStatus = status;
        },
        setBulkTranslationPoller (id: string | null) {
            this.bulkTranslationPoller = id;
        },
        clearBulkTranslationPoller () {
            this.bulkTranslationPoller = null;
        },
        addGuideToMap ({ guide }: { guide: any }) {
            this.map[guide.id] = guide;
        },
        setDescribeYourUseCase ({ describeYourUseCase }: { describeYourUseCase: string }) {
            this.describeYourUseCase = describeYourUseCase;
        },
        updateGuidesMap ({ guide }: { guide: any }) {
            if (guide && !this.map[guide.id]) this.map[guide.id] = {};
            forOwn(guide, (value, key) => {
                (this.map[guide.id] as any)[key] = value;
            });

            if (get(guide, 'id')) {
                forOwn(this.map[guide.id] as any, (_value, key) => {
                    if (!get(guide, key)) delete (this.map[guide.id] as any)[key];
                });
            }

            delete this.guideTranslationErrors[guide?.id];
        },
        removeGuideFromMap ({ guide }: { guide: any }) {
            if (!guide || !guide.id) return;

            delete this.map[guide.id];
        },
        removeStepFromGuide ({ step }: { step: any }) {
            const guideSteps = (this.map[step.guideId] as any).steps;
            const stepIndex = findIndex(guideSteps, ['id', step.id]);
            if (stepIndex > -1) {
                guideSteps.splice(stepIndex, 1);
            }
        },
        addOrUpdateStepOnGuide ({ step }: { step: any }) {
            const guideSteps = (this.map[step.guideId] as any).steps;
            const stepIndex = findIndex(guideSteps, ['id', step.id]);
            if (stepIndex > -1) {
                guideSteps.splice(stepIndex, 1, step);
            } else {
                guideSteps.push(step);
            }
        },
        sortGuideSteps ({ guide }: { guide: any }) {
            guide.steps = sortBy(guide.steps, ['rank']);
        },
        queueAddOrUpdate ({ guide }: { guide: unknown }) {
            this.toAddOrUpdate.push(guide);
        },
        clearQueue () {
            this.toAddOrUpdate.length = 0;
        },
        setHoveredNpsTheme (themeName = '') {
            this.hoveredNpsTheme = themeName;
        },
        setNpsResponsesAggData ({ aggData }: { aggData: AggData }) {
            this.npsResponsesAggData = aggData;
        },
        updateNpsResponseThemes ({ response, newSelectedThemes }: { response: any; newSelectedThemes: unknown[] }) {
            const responseToUpdate = (this.npsResponsesAggData.data as any[]).find(
                (r) => r.browserTime === response.browserTime && r.visitoId === response.VisitorId,
            );

            if (responseToUpdate) {
                responseToUpdate.selectedNpsThemes = newSelectedThemes;
            }
        },
        updateNpsResponsesThemesCounts ({
            currentResponse,
            updatedResponse,
        }: {
            currentResponse: any;
            updatedResponse: any;
        }) {
            let isThemeInCurrentResponse = false;
            let isThemeInUpdatedResponse = false;
            const responseGroupMap: Record<string, string> = {
                Promoter: 'promoters',
                Detractor: 'detractors',
                Passive: 'passives',
            };

            const responseGroup = responseGroupMap[currentResponse.responseGroup];

            this.allNpsThemes = (this.allNpsThemes as any[]).map((themeCounts) => {
                if (themeCounts.name !== npsFilters.noThemeResponses.value) {
                    isThemeInCurrentResponse = currentResponse.selectedNpsThemes.find(
                        (theme: any) => theme.name === themeCounts.name,
                    );
                    isThemeInUpdatedResponse = updatedResponse.resolvedThemes.selected.find(
                        (theme: any) => theme.name === themeCounts.name,
                    );
                } else {
                    isThemeInCurrentResponse = !currentResponse.selectedNpsThemes.length;
                    isThemeInUpdatedResponse = !updatedResponse.resolvedThemes.selected.length;
                }

                const currentResponseThemeCounts = isThemeInCurrentResponse ? 1 : 0;
                const updatedResponseThemeCounts = isThemeInUpdatedResponse ? 1 : 0;

                const delta = updatedResponseThemeCounts - currentResponseThemeCounts;

                return {
                    ...themeCounts,
                    [responseGroup]: themeCounts[responseGroup] + delta,
                };
            });
        },
        updateAllNpsThemeList ({ actionType, theme }: { actionType: string; theme: any }) {
            const { displayName, name, lastUpdatedByUser, lastUpdatedAt } = theme;
            switch (actionType) {
                case 'create':
                    (this.allNpsThemes as any[]).push({
                        name,
                        displayName,
                        passives: 0,
                        promoters: 0,
                        detractors: 0,
                    });
                    break;
                case 'edit':
                    this.allNpsThemes = renameThemeFromList(
                        this.allNpsThemes,
                        name,
                        displayName,
                        lastUpdatedByUser,
                        lastUpdatedAt,
                    );
                    break;
                case 'delete':
                    this.allNpsThemes = deleteThemeFromList(this.allNpsThemes, name);
                    break;
                default:
                    break;
            }
        },
        setNpsThemes (themes: unknown[]) {
            this.allNpsThemes = themes;
        },
        setSideBladeThemeId (themeId: unknown) {
            this.sideBladeThemeId = themeId;
        },
        setSideBladeUpdatedResponse (response: unknown) {
            this.sideBladeUpdatedResponse = response;
        },
        setNpsGptSummary ({ status, text }: { status?: string; text?: string }) {
            this.npsGptSummary = { status, text };
        },
        setAllNpsThemesWithCount () {
            const aggData = this.npsResponsesAggData.data;

            if (aggData && aggData.length > 1) {
                const responsesThemes = (aggData as any[]).find((row) => row.counts);
                const allThemesWithCounts = (this.allNpsThemes as any[]).map((theme) => {
                    const responseTheme = responsesThemes.counts.find((t: any) => t.themeId === theme.name);
                    const noResponsesTheme = {
                        displayName: theme.displayName,
                        name: theme.name,
                        passives: 0,
                        promoters: 0,
                        detractors: 0,
                    };

                    return responseTheme
                        ? {
                            ...responseTheme,
                            name: responseTheme.themeId,
                            displayName: getThemeDisplayName({
                                displayName: responseTheme?.npsTheme,
                                name: responseTheme.themeId,
                            }),
                            lastUpdatedByUser: theme.lastUpdatedByUser,
                            lastUpdatedAt: theme.lastUpdatedAt,
                        }
                        : noResponsesTheme;
                });
                this.allNpsThemes = allThemesWithCounts;
            }
        },
        setPreviewLanguage ({ previewLanguage }: { previewLanguage: string }) {
            this.previewLanguage = previewLanguage;
        },
        setShouldUpdateNpsResponses (shouldUpdate: boolean) {
            this.shouldUpdateNpsResponses = shouldUpdate;
        },
        setClassicDesignerTemplates (classicDesignerTemplates: unknown[]) {
            this.classicDesignerTemplates = classicDesignerTemplates;
        },
        loadAll (
            {
                reFetch = false,
                expandKey = '*',
                includeArchived = true,
                includeMultiApp = true,
                appId,
            }: {
                reFetch?: boolean;
                expandKey?: string;
                includeArchived?: boolean;
                includeMultiApp?: boolean;
                appId?: unknown;
            } = {},
        ) {
            if (loadAllPromise && !reFetch) {
                return loadAllPromise;
            }

            this.setFetching({ isFetching: true });

            loadAllPromise = loadGuides({ expandKey, includeArchived, includeMultiApp, appId });

            return (loadAllPromise as Promise<any>)
                .then(({ data: guidesList }: { data: unknown[] }) => {
                    this.setMap({ map: keyBy(guidesList, 'id') });
                    this.setFetching({ isFetching: false });

                    return guidesList;
                })
                .catch(() => {
                    loadAllPromise = null;
                    this.setFetching({ isFetching: false });
                });
        },
        async loadClassicDesignerTemplates () {
            const existingClassicDesignerTemplates = this.classicDesignerTemplates;
            if (existingClassicDesignerTemplates && existingClassicDesignerTemplates.length) {
                return existingClassicDesignerTemplates;
            }

            const { data: classicDesignerTemplates } = await getClassicDesignerTemplates();
            this.setClassicDesignerTemplates(classicDesignerTemplates);

            return undefined;
        },
        async loadOne (
            {
                setActive = false,
                reFetch = false,
                id,
                tag,
            }: { setActive?: boolean; reFetch?: boolean; id?: string; tag?: string } = {},
        ) {
            const guideInState = this.getGuideById(id as string);
            if ((!tag && !reFetch && !!guideInState) || !id) {
                if (setActive) this.setActiveGuide({ guide: guideInState });

                return Promise.resolve();
            }

            this.setFetching({ isFetching: true });

            try {
                const { data: guide } = await loadGuide({ id, tag });
                this.setFetching({ isFetching: false });
                this.addOrUpdateMap({ guide });
                if (setActive) {
                    this.setActiveGuide({ guide });
                }
            } catch {
                this.setFetching({ isFetching: false });
                if (setActive) {
                    this.setActiveGuide({ guide: null });
                }
            }
        },
        async loadLocalizedGuideContent ({ guideId }: { guideId: string }) {
            const { data: guide } = await loadGuide({ id: guideId } as any);

            const translatedStepsWithContent = await getGuideStepsContent((guide as any).steps);

            set(guide as any, 'steps', translatedStepsWithContent);

            this.setActiveGuide({ guide });
            this.updateGuidesMap({ guide });
        },
        async startBulkTranslateGuide ({ guideId, langCodes }: { guideId: string; langCodes: string[] }): Promise<null> {
            this.bulkTranslationLangCodes = langCodes;
            this.setBulkTranslationStatus({ status: { guideId, state: 'running' } });
            runningBulkTranslationGuideIds.add(guideId);

            try {
                await bulkTranslateGuideUtil(guideId, langCodes);
            } catch (error: unknown) {
                const httpError = error as HttpErrorLike;

                if (httpError?.response?.status !== 409) {
                    this.bulkTranslationLangCodes = [];
                    this.setBulkTranslationStatus({ status: { guideId, state: 'failed', message: httpError?.message } });
                    runningBulkTranslationGuideIds.delete(guideId);

                    return null;
                }
            }

            return this.checkBulkTranslationStatus({ guideId, langCodes });
        },
        // Checks the guide's bulk translate status once; if the job is still running, keeps
        // polling until it succeeds, fails, or the owning component unmounts (clearBulkTranslationPoller).
        // langCodes marks which languages to flag as in-flight if a job turns out to be running —
        // pass the guide's current untranslated languages when checking on mount, so a job started
        // elsewhere (e.g. before a page reload) still lights up the per-row "Translating..." state.
        async checkBulkTranslationStatus ({ guideId, langCodes = [] }: { guideId: string; langCodes?: string[] }): Promise<null> {
            const existingStatusPromise = bulkTranslationStatusPromises[guideId];

            if (existingStatusPromise) {
                // Don't let a second caller's await reject unhandled — the original caller below
                // already owns error handling/state updates for this shared poll.
                await existingStatusPromise.catch(() => null);

                return null;
            }

            let observedRunning = runningBulkTranslationGuideIds.has(guideId);

            const shouldStopPolling = ({ response, error }: { response?: BulkTranslateGuideStatusResponse; error?: unknown }) => {
                if (this.bulkTranslationPoller !== guideId) return true;
                if (error || !response) return true;

                if (response.data.state === 'running') {
                    observedRunning = true;
                    runningBulkTranslationGuideIds.add(guideId);
                    this.bulkTranslationLangCodes = langCodes;
                    this.setBulkTranslationStatus({ status: { ...response.data, guideId } });

                    return false;
                }

                this.setBulkTranslationStatus({ status: observedRunning ? { ...response.data, guideId } : null });

                return true;
            };

            this.setBulkTranslationPoller(guideId);
            const statusPromise = pollBulkTranslateGuideStatus(guideId, shouldStopPolling);
            bulkTranslationStatusPromises[guideId] = statusPromise;

            try {
                await statusPromise;

                if (this.bulkTranslationPoller === guideId && this.bulkTranslationStatus?.state === 'succeeded') {
                    await this.loadLocalizedGuideContent({ guideId });
                }
            } catch (error: unknown) {
                const httpError = error as HttpErrorLike;

                if (this.bulkTranslationPoller !== guideId) {
                    // a newer check for a different guide took over the shared poller; leave its state alone
                } else if (httpError?.response?.status === 404) {
                    // No bulk translate history for this guide — reset rather than leaving a stale
                    // "running" status (set optimistically by startBulkTranslateGuide) stuck forever.
                    this.setBulkTranslationStatus({ status: null });
                } else if (observedRunning) {
                    this.setBulkTranslationStatus({ status: { guideId, state: 'failed', message: httpError?.message } });
                } else {
                    this.setBulkTranslationStatus({ status: null });
                }
            } finally {
                delete bulkTranslationStatusPromises[guideId];
                // Only touch shared state if we're still the poller owner — a newer check for a
                // different guide may have already taken over, and we shouldn't clobber that one.
                // A cancelled check never learns whether its job resolved, so its guideId must stay
                // marked running for the next check to pick the transition back up correctly.
                if (this.bulkTranslationPoller === guideId) {
                    runningBulkTranslationGuideIds.delete(guideId);
                    this.bulkTranslationLangCodes = [];
                    this.clearBulkTranslationPoller();
                }
            }

            return null;
        },
        reloadGuides () {
            return this.loadAll({ reFetch: true });
        },
        setGuideForEditing ({ guide }: { guide: unknown }) {
            this.setActiveGuide({ guide });
        },
        addOrUpdateMap ({ guide }: { guide: any }) {
            if (this.fetching) {
                this.queueAddOrUpdate({ guide });
            } else if (this.getGuideById(guide.id)) {
                this.updateGuidesMap({ guide });
            } else {
                this.addGuideToMap({ guide });
            }
        },
        updateFromQueue () {
            (this.toAddOrUpdate as any[]).forEach((guide) => {
                this.addOrUpdateMap({ guide });
            });
        },
        updateLaunchMethods ({ launchMethodMap }: { launchMethodMap: Record<string, unknown> }) {
            for (const [guideId, launchMethod] of Object.entries(launchMethodMap)) {
                if (this.map[guideId]) {
                    this.updateGuidesMap({
                        guide: {
                            ...(this.map[guideId] as any),
                            launchMethod,
                        },
                    });
                }
            }
        },
        async saveGuide ({
            guide: guideToSave,
            buildContent = false,
            preserveUpdateInfo = false,
            isOrgResource = false,
            location = '',
        }: {
            guide: any;
            buildContent?: boolean;
            preserveUpdateInfo?: boolean;
            isOrgResource?: boolean;
            location?: string;
        }) {
            if (isFunction(get(guideToSave, 'showsAfter.valueOf'))) {
                guideToSave.showsAfter = guideToSave.showsAfter.valueOf();
            }
            this.setSaving({ isSaving: true });

            let guideListenPortalIds: string[] = [];
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useAppsStore } = require('@/stores/apps.store');
            const appsStore = useAppsStore();
            const app = appsStore.getAppById(guideToSave.appId);
            if (Apps.isMobile(app)) {
                guideToSave.steps.forEach((step: any) => {
                    const stepListenPortalIds = GuideService.extractListenPortalIds({ step });
                    step.attributes.launchListenPortal = stepListenPortalIds.length ? stepListenPortalIds : false;

                    guideListenPortalIds = stepListenPortalIds
                        ? guideListenPortalIds.concat(stepListenPortalIds)
                        : guideListenPortalIds;
                });

                if (!guideToSave.minSdkVersion) {
                    try {
                        const steps = guideToSave.steps.map((step: any) => ({
                            buildingBlocks: typeof step.buildingBlocks === 'string'
                                ? JSON.parse(step.buildingBlocks)
                                : step.buildingBlocks,
                        }));
                        const isAppFlutterCodeless =
                            app.framework === 'flutter' && !appsStore.isRestrictedMobileApp(app.id);
                        guideToSave.minSdkVersion =
                            getMinSdkVersionForGuide(
                                { ...guideToSave, steps },
                                ROOT_MIN_SDK_VERSION,
                                app.platform,
                                undefined,
                                { isAppFlutterCodeless },
                            ) || ROOT_MIN_SDK_VERSION;
                    } catch {
                        guideToSave.minSdkVersion = ROOT_MIN_SDK_VERSION;
                    }
                }
            }

            try {
                let { data: guide } = await saveGuideUtil({ guide: guideToSave, preserveUpdateInfo, isOrgResource });
                if (buildContent) {
                    guide = await getContent(guide);
                }
                const { redisplay } = guideToSave;
                if (redisplay) {
                    await saveGuideRedisplay({ guideId: guide.id, redisplay });
                    guide.redisplay = redisplay;
                }

                if (Apps.isMobile(app) && guideListenPortalIds.length) {
                    guideListenPortalIds = uniq(guideListenPortalIds);
                    GuideService.updateGuidesPerPortal(http, {
                        savedPortalIds: [],
                        newPortalIds: guideListenPortalIds,
                        guideId: guide.id,
                    });
                }

                this.addOrUpdateMap({ guide });
                this.setActiveGuide({ guide });
                this.setSaving({ isSaving: false });

                fireGuideCreatedTrackEvent(guide, location);

                return guide;
            } catch (error: any) {
                this.setSaving({ isSaving: false });

                if (error?.response?.status === 426 && guideToSave?.attributes?.resourceCenter) {
                    throw error;
                }
            }
        },
        async createGuideFromHtml ({
            rawHtmls,
            appId,
            theme,
            guideParam = {},
            location = '',
        }: {
            rawHtmls: unknown;
            appId: unknown;
            theme: unknown;
            guideParam?: Record<string, unknown>;
            location?: string;
        }) {
            this.setSaving({ isSaving: true });

            try {
                const { data: guide } = await createGuideFromHtmlUtil({ rawHtmls, appId, theme, guide: guideParam });

                this.addOrUpdateMap({ guide });
                this.setActiveGuide({ guide });
                this.setSaving({ isSaving: false });

                fireGuideCreatedTrackEvent(guide, location);

                return guide;
            } catch (error) {
                this.setSaving({ isSaving: false });

                throw error;
            }
        },
        async patchGuide ({
            guide,
            remoteGuide,
            fieldsToPatch,
            buildContent = false,
            skipSetActive = false,
        }: {
            guide: any;
            remoteGuide?: any;
            fieldsToPatch?: string[];
            buildContent?: boolean;
            skipSetActive?: boolean;
        }) {
            this.setError({ error: null });
            if (guide.state === GUIDE_STATE.deleted && !this.map[guide.id]) return;
            if (isFunction(get(guide, 'showsAfter.valueOf'))) {
                guide.showsAfter = guide.showsAfter.valueOf();
            }
            this.setSaving({ isSaving: true });

            if (!remoteGuide) {
                remoteGuide = cloneDeep(guide);
            }
            const guideFieldsToPatchDefaults = [
                'audience',
                'audienceUiHint',
                'emailConfiguration',
                'emailState',
                'expiresAfter',
                'name',
                'polls',
                'recurrence',
                'recurrenceEligibilityWindow',
                'showsAfter',
                'state',
                'description',
            ];
            const guideFieldsToPatch = fieldsToPatch || guideFieldsToPatchDefaults;
            try {
                const response = await GuideService.patchGuideFields(http, { guide, remoteGuide, guideFieldsToPatch });
                let updatedGuide;
                if (guide.translationStates && !response.data.translationStates) {
                    updatedGuide = response.data;
                } else {
                    updatedGuide = Object.assign({}, guide, response.data);
                }

                if (buildContent) {
                    updatedGuide = await getContent(updatedGuide);
                }

                if (!skipSetActive) this.setActiveGuide({ guide: updatedGuide });
                this.updateGuidesMap({ guide: updatedGuide });
                this.setSaving({ isSaving: false });

                this.patchGuideTrackEvents({ updatedGuide, remoteGuide, guide });

                return updatedGuide;
            } catch (err) {
                this.setError({ error: err });
                this.setSaving({ isSaving: false });
            }
        },
        async setGuideDNT ({ guideId, isDNT }: { guideId: string; isDNT: boolean }) {
            this.setError({ error: null });
            this.setSaving({ isSaving: true });
            try {
                await setGuideDNTUtil(guideId, isDNT);
                const guide = this.getGuideById(guideId) as any;
                guide.isDNT = isDNT;
                this.setActiveGuide({ guide });
                this.updateGuidesMap({ guide });
                this.setSaving({ isSaving: false });

                return guide;
            } catch (err) {
                this.setError({ error: err });
                this.setSaving({ isSaving: false });
            }
        },
        async saveGuideRedisplay ({ guideId, redisplay }: { guideId: string; redisplay: unknown }) {
            this.setError({ error: null });
            this.setSaving({ isSaving: true });
            try {
                if (redisplay) {
                    await saveGuideRedisplay({ guideId, redisplay });
                } else {
                    await deleteGuideRedisplay({ guideId });
                }
                const guide = this.getGuideById(guideId) as any;
                guide.redisplay = redisplay;
                this.setActiveGuide({ guide });
                this.updateGuidesMap({ guide });
                this.setSaving({ isSaving: false });

                return guide;
            } catch (err) {
                this.setError({ error: err });
                this.setSaving({ isSaving: false });
            }
        },
        async bulkArchiveGuides ({ guides }: { guides: any[] }) {
            return this._bulkPatchGuidesToState(guides, GUIDE_STATE.archived);
        },
        async bulkRestoreGuides ({ guides }: { guides: any[] }) {
            return this._bulkPatchGuidesToState(guides, GUIDE_STATE.disabled);
        },
        async _bulkPatchGuidesToState (guides: any[], state: string) {
            const BULK_PATCH_BATCH_SIZE = 100;
            const settled = await Promise.allSettled(
                chunk(guides, BULK_PATCH_BATCH_SIZE).map((batch) =>
                    bulkPatchGuides({
                        requests: batch.map((guide) => ({
                            id: guide.id,
                            patch: [{ op: 'replace', path: '/state', value: state }],
                        })),
                    }),
                ),
            );

            const resultsById: Record<string, any> = settled.reduce((results: Record<string, any>, outcome) => {
                if (outcome.status === 'fulfilled') {
                    ((outcome.value as any).data || []).forEach((result: any) => {
                        results[result.id] = result;
                    });
                }

                return results;
            }, {});

            return guides.map((guide) => {
                const result = resultsById[guide.id];
                const succeeded = Boolean(result) && result.code >= 200 && result.code < 300;

                if (succeeded) {
                    const existingGuide = this.getGuideById(guide.id);
                    const updatedGuide = { ...(existingGuide || guide), state };

                    if (existingGuide) {
                        this.updateGuidesMap({ guide: updatedGuide });
                    }

                    this.patchGuideTrackEvents({ updatedGuide, remoteGuide: guide, guide });

                    return { guide, status: 'success' };
                }

                return { guide, status: 'failure' };
            });
        },
        patchGuideAndSteps ({
            guide,
            guideFieldsToPatch,
            stepFieldsToPatch,
        }: {
            guide: any;
            guideFieldsToPatch: string[];
            stepFieldsToPatch: string[];
        }) {
            const remoteGuide = cloneDeep(guide);
            let newGuide: any;

            this.setSaving({ isSaving: true });

            return GuideService.patchGuideFields(http, { guide, remoteGuide, guideFieldsToPatch })
                .then((response: any) => {
                    newGuide = response.data;

                    return GuideService.patchGuideSteps(http, { guide, remoteGuide: newGuide, stepFieldsToPatch });
                })
                .then((newStepResponses: any[]) => {
                    newGuide.steps = newStepResponses.map((response) => response.data);

                    const updatedGuide = Object.assign({}, guide, newGuide);

                    this.updateGuidesMap({ guide: updatedGuide });
                    this.setSaving({ isSaving: false });

                    return updatedGuide;
                })
                .catch(() => {
                    this.setSaving({ isSaving: false });
                });
        },
        patchStepOnGuide ({
            updatedStep,
            remoteGuide,
            stepFieldToPatch,
        }: {
            updatedStep: any;
            remoteGuide: any;
            stepFieldToPatch: string;
        }) {
            this.setSaving({ isSaving: true });

            return GuideService.patchGuideStep(http, { updatedStep, remoteGuide, stepFieldToPatch })
                .then(({ data: step }: { data: any }) => {
                    this.addOrUpdateStepOnGuide({ step });
                    if (stepFieldToPatch === 'rank') {
                        this.sortGuideSteps({ guide: remoteGuide });
                    }

                    const updatedGuide = this.getGuideById(remoteGuide.id);
                    this.setActiveGuide({ guide: updatedGuide });
                    this.updateGuidesMap({ guide: updatedGuide });
                    this.setSaving({ isSaving: false });

                    return updatedGuide;
                })
                .catch(() => {
                    this.setSaving({ isSaving: false });
                });
        },
        async remove ({ guide }: { guide: any }) {
            this.setFetching({ isFetching: true });
            try {
                await deleteGuide({ id: guide.id });
                this.removeGuideFromMap({ guide });

                this.setFetching({ isFetching: false });
            } catch (err) {
                this.setFetching({ isFetching: false });
                throw err;
            }
        },
        async saveGuideStep ({ remoteGuide, updatedStep }: { remoteGuide: any; updatedStep: any }) {
            this.setSaving({ isSaving: true });

            try {
                const { data: step } = await saveStep({ guide: remoteGuide, step: updatedStep });
                this.addOrUpdateStepOnGuide({ step });
                this.setActiveGuide({ guide: this.getGuideById(remoteGuide.id) });
                this.setSaving({ isSaving: false });

                return step;
            } catch (error) {
                this.setSaving({ isSaving: false });

                return error;
            }
        },
        async removeGuideStep ({ remoteGuide, step }: { remoteGuide: any; step: any }) {
            this.setSaving({ isSaving: true });

            try {
                await deleteStep({ guide: remoteGuide, step });
                this.removeStepFromGuide({ step });
                this.setActiveGuide({ guide: this.getGuideById(remoteGuide.id) });
                this.setSaving({ isSaving: false });
            } catch (error) {
                this.setSaving({ isSaving: false });

                return error;
            }
        },
        async importGuideLocalization ({
            guideId,
            langCode,
            translations,
        }: {
            guideId: string;
            langCode: string;
            translations: unknown;
        }) {
            this.setSaving({ isSaving: true });

            try {
                await saveGuideTranslations(guideId, langCode, translations);
                await this.loadLocalizedGuideContent({ guideId });
            } finally {
                this.setSaving({ isSaving: false });
            }
        },
        async importGuideLocalizationFile ({
            guideId,
            langCode,
            file,
        }: {
            guideId: string;
            langCode: string;
            file: unknown;
        }) {
            const subscriptionId = useSubscriptionStore().active.id;
            this.setSaving({ isSaving: true });

            try {
                await importLocalizationFile(subscriptionId, guideId, langCode, file);
                await this.loadLocalizedGuideContent({ guideId });
            } finally {
                this.setSaving({ isSaving: false });
            }
        },
        async clearGuideTranslationStrings ({
            guideId,
            langCode,
        }: {
            guideId: string;
            langCode: string;
        }) {
            this.setSaving({ isSaving: true });

            try {
                await clearTranslatedStrings(guideId, langCode);
                await this.loadLocalizedGuideContent({ guideId });
            } finally {
                this.setSaving({ isSaving: false });
            }
        },
        async addMinAgentVersion ({
            guideId,
            minAgentVersion,
        }: {
            guideId: string;
            minAgentVersion: unknown;
        }) {
            const guide = this.getGuideById(guideId);
            let guideToSave = cloneDeep(guide);
            guideToSave = MinAgentVersion.addMinAgentVersion(guideToSave, minAgentVersion);
            await this.patchGuide({
                guide: guideToSave,
                remoteGuide: guide,
                fieldsToPatch: ['minAgentVersion', 'attributes'],
            });
        },
        async removeMinAgentVersion ({
            guideId,
            minAgentVersion,
        }: {
            guideId: string;
            minAgentVersion: unknown;
        }) {
            const guide = this.getGuideById(guideId);
            let guideToSave = cloneDeep(guide);
            guideToSave = MinAgentVersion.removeMinAgentVersion(guideToSave, minAgentVersion);
            await this.patchGuide({
                guide: guideToSave,
                remoteGuide: guide,
                fieldsToPatch: ['minAgentVersion', 'attributes'],
            });
        },
        saveNpsResponsesAggData ({ status, data = [] }: { status: string; data?: unknown[] }) {
            const aggData = { status, data };
            this.setNpsResponsesAggData({ aggData });
            this.setAllNpsThemesWithCount();
        },
        fetchNpsResponses ({
            component,
            aggConfigField,
            store,
        }: {
            component: any;
            aggConfigField: unknown;
            store: unknown;
        }) {
            this.saveNpsResponsesAggData({ status: 'loading' });
            const withComponentParam = { config: aggConfigField };
            const aggConnectors = [withComponent(withComponentParam)];

            if (!component.widget) {
                aggConnectors.push(
                    withStore({
                        blacklist: () => useGlobalFiltersStore().segmentBlacklist,
                        timeSeries: () => useGlobalFiltersStore().timeSeriesSpec,
                        segmentId: () => useGlobalFiltersStore().segmentId,
                        includeAnonymous: () => useGlobalFiltersStore().segmentIncludeAnonymous,
                        selectedAccount: () => useGlobalFiltersStore().selectedAccountAccount,
                    }),
                );
            }

            return connectAggregationToState(NpsPollResponsesAgg, ...aggConnectors).bind(component)({
                component,
                store,
            });
        },
        async fetchGuideNpsThemes ({ guideId }: { guideId: string }) {
            try {
                this.setNpsThemes(await getAllNpsThemes(guideId));
            } catch {
                throw new Error('Failed to retrieve NPS Themes');
            }
        },
        async fetchNpsGptSummary ({
            theme,
            ignoreCache,
        }: {
            theme: any;
            ignoreCache: boolean;
        }) {
            const { RESOLVED, FAILED, LOADING, INSUFFICIENT_RESPONSES } = NPS_GPT_SUMMARY_STATUS;
            this.setNpsGptSummary({ status: LOADING });
            setTimeout(async () => {
                try {
                    const aggData = this.getNpsResponsesAggData.data;
                    if (aggData && aggData.length) {
                        const themeResponses = (aggData as any[])
                            .filter((row) => row.selectedNpsThemes.some((t: any) => t.name === theme.name))
                            .slice(0, gptSummaryMaxResponsesLimit);

                        const textualResponses = themeResponses.map((response) => response.followUpResponse);

                        if (textualResponses.length >= gptSummaryMinResponsesLimit) {
                            this.setNpsGptSummary({
                                text: await getGptSummaryText({
                                    textualResponses,
                                    theme: theme.displayName,
                                    ignoreCache,
                                }),
                                status: RESOLVED,
                            });
                        } else {
                            this.setNpsGptSummary({
                                text: '',
                                status: INSUFFICIENT_RESPONSES,
                            });
                        }
                    }
                } catch {
                    this.setNpsGptSummary({
                        text: '',
                        status: FAILED,
                    });
                }
            }, 1200);
        },
        clearNpsGptSummary () {
            this.setNpsGptSummary({ text: '' });
        },
        async updateGuideName ({
            id,
            name,
            skipSetActive = false,
        }: {
            id: string;
            name: string;
            skipSetActive?: boolean;
        }) {
            const guideToUpdate = cloneDeep(this.getGuideById(id)) as any;
            guideToUpdate.name = name;

            await this.patchGuide({
                guide: guideToUpdate,
                fieldsToPatch: ['name'],
                skipSetActive,
            });
        },
        async cloneGuide ({
            guide,
            app,
            options = {},
        }: {
            guide: any;
            app: any;
            options?: Record<string, unknown>;
        }) {
            if (!guide || !app) throw new Error('Guide and App are required to clone a guide');
            const {
                crossPlatform,
                useDefaultTheme,
                cloneCodeBlock,
                cloneCategory,
                cloneProductArea,
                isCloningIntoJourney = false,
                name = '',
                cloneLocalizationFiles = true,
            } = options as any;

            const isDifferentAppChosen = guide.appId !== parseInt(app.id);
            let tempGuide = cloneDeep(guide);
            let savedGuide: any;

            if (!isDifferentAppChosen && !isCloningIntoJourney) {
                savedGuide = await cloneGuideViaEndpoint(guide.id, { name, cloneLocalizationFiles });
                if (savedGuide) {
                    this.addOrUpdateMap({ guide: savedGuide });
                }
            } else {
                if (!isCloningIntoJourney && tempGuide.attributes?.journeyId) {
                    delete tempGuide.attributes.journeyId;
                }
                if (isCloningIntoJourney) {
                    if (tempGuide.audience) {
                        delete tempGuide.audience;
                    }
                    tempGuide.audienceUiHint = { filters: [] };
                }
                if (guide.automations?.length > 0) {
                    tempGuide.automations = Automations.removeElementPathRulesFromFeatureAutomations(
                        tempGuide.automations,
                    );
                }
                if (isDifferentAppChosen) {
                    tempGuide = updateClonedGuideActivationLocation(tempGuide, isMobileApp(app.platform));
                }

                if (isDoNotTrackGuide(tempGuide)) {
                    tempGuide.isDNT = false;
                }

                if (name) tempGuide.name = name;

                const clonedGuide = await BuildingBlockGuidesService.copy({
                    guide: { ...tempGuide, appId: parseInt(app.id) },
                    removeTemplateChildren: isDifferentAppChosen,
                });

                removeLaunchMethodFromGuide(clonedGuide, 'launcher');

                const defaultTheme = useThemesStore().getDefaultThemeForAppId((clonedGuide as any).appId);

                BuildingBlockPropertiesService.clone({
                    guide: clonedGuide,
                    platform: app.platform,
                    preventCodeBlock: !cloneCodeBlock,
                    crossPlatform,
                    isNpsGuide: isNpsGuide(clonedGuide),
                    ...(isDifferentAppChosen && useDefaultTheme && { theme: defaultTheme }),
                });

                savedGuide = await this.saveGuide({ guide: clonedGuide });

                // Re-send showsAfter unchanged purely to trigger guide3Patch's recalculation/retranslation of attributes.dates.
                if (isDifferentAppChosen && savedGuide && this.isLocalizationEnabled(savedGuide.appId)) {
                    savedGuide =
                        (await this.patchGuide({
                            guide: savedGuide,
                            fieldsToPatch: ['showsAfter'],
                            skipSetActive: true,
                        })) || savedGuide;
                }
            }

            if (savedGuide) {
                this.setActiveGuide({ guide: savedGuide });

                if (cloneProductArea) {
                    const groupsStore = useGroupsStore();
                    const productArea = groupsStore.groupByGuideId({ guideId: tempGuide.id });
                    if (productArea && productArea.id) {
                        await groupsStore.addItemToGroup({
                            groupId: productArea.id,
                            itemType: 'guide',
                            itemId: savedGuide.id,
                        });
                    }

                    savedGuide.group = Object.assign({}, productArea);
                }

                if (cloneCategory) {
                    const guideCategoriesStore = useGuideCategoriesStore();
                    const category = guideCategoriesStore.guideCategoryByGuideId({ guideId: tempGuide.id });
                    if (category && (category as any).id) {
                        await guideCategoriesStore.addGuideToCategory({
                            categoryId: (category as any).id,
                            guideId: savedGuide.id,
                        });
                    }
                }

                if (isDifferentAppChosen) {
                    // eslint-disable-next-line @typescript-eslint/no-require-imports
                    const { useAppsStore } = require('@/stores/apps.store');
                    const cloneAppsStore = useAppsStore();
                    const fromApp = cloneAppsStore.getAppById(tempGuide.appId);
                    const toApp = cloneAppsStore.getAppById(savedGuide.appId);

                    window.pendo.track('cloneGuideAnotherApp', {
                        fromAppId: tempGuide.appId,
                        toAppId: savedGuide.appId,
                        fromOs: fromApp?.platform,
                        toOs: toApp?.platform,
                    });
                }
            }

            return savedGuide;
        },
        async patchGuideTrackEvents ({
            updatedGuide,
            remoteGuide,
            guide,
        }: {
            updatedGuide: any;
            remoteGuide: any;
            guide: any;
        }) {
            const needsVisitorCount = updatedGuide?.state === 'public' && updatedGuide?.state !== remoteGuide?.state;
            let numOfVisitorsInSegment: number | undefined;
            try {
                if (needsVisitorCount) {
                    // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
                    const { useSegmentsStore } = require('@/stores/segments.store'); // TODO: remove require when circular dep between guides↔segments↔surveys is resolved
                    const aggParams = await useSegmentsStore().getGuideSegmentVisitorCountAggParams({ guide });
                    const { targeted, targetedInSegment } = await getGuideSegmentVisitorCount({
                        guide: updatedGuide,
                        aggParams,
                    });
                    numOfVisitorsInSegment = isNumber(targeted) ? targeted : targetedInSegment;
                }
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                const { useAppsStore } = require('@/stores/apps.store');
                const app = useAppsStore().getAppById(updatedGuide.appId);
                const guideCategoryName = (useGuideCategoriesStore().guideCategoryByGuideId({
                    guideId: updatedGuide.id,
                }) as any)?.name;
                fireGuideTrackEvents(updatedGuide, remoteGuide, numOfVisitorsInSegment, app, guideCategoryName);
            } catch (error) {
                console.error('Error firing track event', error);
            }
        },
    },
});

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
    Object.keys(bulkTranslationStatusPromises).forEach((guideId) => delete bulkTranslationStatusPromises[guideId]);
    runningBulkTranslationGuideIds.clear();
}

export function __markBulkTranslationGuideRunningForUnitTests (guideId: string) {
    runningBulkTranslationGuideIds.add(guideId);
}

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useGuidesStore, module.hot));
}
